// ─── ApiError ────────────────────────────────────────────────────────────────

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly data: unknown;
  readonly isClientError: boolean;
  readonly isServerError: boolean;

  constructor(message: string, status: number, code = 'UNKNOWN_ERROR', data?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.data = data;
    this.isClientError = status >= 400 && status < 500;
    this.isServerError = status >= 500;
  }

  static fromResponse(response: {
    status: number;
    data?: { message?: string; code?: string; errors?: unknown };
  }): ApiError {
    const { status, data } = response;
    const message = data?.message ?? httpStatusMessage(status);
    const code = data?.code ?? `HTTP_${status}`;
    return new ApiError(message, status, code, data?.errors);
  }

  toString(): string {
    return `ApiError [${this.status}] ${this.code}: ${this.message}`;
  }
}

// ─── Validation error shape from API ─────────────────────────────────────────

export interface ApiValidationError {
  field: string;
  message: string;
  code?: string;
}

// ─── Status message map ───────────────────────────────────────────────────────

const STATUS_MESSAGES: Readonly<Record<number, string>> = {
  400: 'Bad request',
  401: 'Session expired. Please log in again.',
  403: 'You do not have permission to perform this action.',
  404: 'The requested record was not found.',
  409: 'A conflict occurred — the record may already exist.',
  422: 'Validation failed. Please check the highlighted fields.',
  429: 'Too many requests. Please wait and try again.',
  500: 'Server error. Please try again or contact support.',
  502: 'Service temporarily unreachable.',
  503: 'Service is under maintenance. Please try again shortly.',
};

function httpStatusMessage(status: number): string {
  return STATUS_MESSAGES[status] ?? 'An unexpected error occurred.';
}
